/**
 * Which recorded mouth to show, frame by frame, from the reply's own audio (docs/NEW-UI/09).
 *
 * The library (avatar/tools/viseme_bake) holds every plate frame × the mouths MuseTalk drew on held sounds: the
 * vowels (a e i o u, the open eh and oh), the lips (m, f, v) and the teeth and tongue (s, sh, z, n, l); `rest` is the
 * plate's own mouth, drawn as it is. Which one goes with a moment of speech is learnt from MuseTalk itself (teach.py: on 40
 * sentences in it/en/de, the library's mouth nearest to the one it drew, and how far each other one was), into a
 * model (tools/mouth-learn.ts → mouths.json): a few hundred prototypes of what the audio looks like around a frame,
 * each with the mean distance of every mouth to what MuseTalk drew there. A frame takes the mouth nearest on average
 * over its nearest prototypes; a frame alone between two that agree then takes their mouth, and a mouth shorter than
 * the model's `hold` the one before it (measured on the lips: MuseTalk's pace at 2 frames, jerkier at 1).
 *
 * What the audio looks like (`frameFeatures`): the spectral envelope (LPC, on the Bark scale), the level against the
 * clause's loudest frame, voicing and brightness. Around a frame (`frameVector`): ahead of it, because the mouth
 * moves before the sound (measured: MuseTalk's mouth matches the audio ~120 ms later best), and its neighbours.
 * Hand-written rules on the same features were measured worse than always showing a closed mouth (22/10).
 */

export type Mouth = 'rest' | 'm' | 'a' | 'e' | 'i' | 'o' | 'u' | 'f' | 's' | 'sh' | 'z' | 'n' | 'l' | 'v' | 'eh' | 'oh';

export const FRAME_MS = 40;

const MOUTHS: readonly Mouth[] = ['rest', 'm', 'a', 'e', 'i', 'o', 'u', 'f', 's', 'sh', 'z', 'n', 'l', 'v', 'eh', 'oh'];

/** A learnt choice of mouth (mouths.json): how a frame's vector is made, and the prototypes it is compared with. */
export interface MouthModel {
  version: 2;
  /** The order of every prototype's `d`. */
  mouths: Mouth[];
  /** Frames ahead whose audio the mouth of a frame is read from. */
  lookahead: number;
  /** The neighbours (frame offsets from the read one) whose audio comes too. */
  context: number[];
  /** Prototypes averaged per frame. */
  k: number;
  /** The fewest frames a mouth is shown. */
  hold: number;
  /** A vector like `frameVector`'s, the mean distance of each mouth to what MuseTalk drew, how many frames it sums. */
  prototypes: { f: number[]; d: number[]; n: number }[];
}

/** The model of a library's mouths.json; anything else is refused, never half-read. */
export function modelOf(json: unknown): MouthModel {
  const fail = (why: string): never => {
    throw new Error(`mouths.json: ${why}`);
  };
  const m = (json ?? {}) as Partial<MouthModel>;
  if (m.version !== 2) fail('not a version 2 model');
  const mouths = Array.isArray(m.mouths) && m.mouths.length && m.mouths.every((x) => MOUTHS.includes(x)) ? m.mouths : fail('mouths');
  const context = Array.isArray(m.context) && m.context.every(Number.isInteger) ? m.context : fail('context');
  if (!Number.isInteger(m.lookahead)) fail('lookahead');
  if (!Number.isInteger(m.k) || m.k! < 1) fail('k');
  if (!Number.isInteger(m.hold) || m.hold! < 1) fail('hold');
  const prototypes = Array.isArray(m.prototypes) && m.prototypes.length ? m.prototypes : fail('no prototypes');
  const width = vectorWidth(context.length);
  for (const p of prototypes) {
    if (p.f?.length !== width || p.d?.length !== mouths.length || !(p.n > 0)) fail('a prototype of the wrong shape');
    if (![...p.f, ...p.d].every(Number.isFinite)) fail('a prototype with a number that is not');
  }
  return m as MouthModel;
}

/** One mouth per FRAME_MS of `pcm` (mono, -1..1), the last frame partial. */
export function mouthTrack(pcm: Float32Array, rate: number, model: MouthModel): Mouth[] {
  const features = frameFeatures(pcm, rate);
  const raw = features.map((_, k) => nearestMouth(frameVector(features, k + model.lookahead, model), model));
  return held(smooth(raw), model.hold);
}

/** A run of the same mouth shorter than `frames` takes the mouth of the run before it (the first run stays). */
export function held(track: Mouth[], frames: number): Mouth[] {
  const out = [...track];
  for (let start = 0; start < out.length; ) {
    let end = start;
    while (end < out.length && out[end] === out[start]) end++;
    if (end - start < frames && start > 0) out.fill(out[start - 1]!, start, end);
    start = end;
  }
  return out;
}

/** A frame alone between two that agree takes their mouth. Read on the track as it was, so nothing cascades: a
 * mouth that changes every frame stays as it is (it did cascade, and the start of a sentence became one silence). */
export function smooth(track: Mouth[]): Mouth[] {
  return track.map((m, k) => (k > 0 && k < track.length - 1 && track[k - 1] === track[k + 1] ? track[k - 1]! : m));
}

/** The mouth nearest on average, over the model's `k` prototypes nearest to `v` (each weighted by its frames). */
function nearestMouth(v: number[], model: MouthModel): Mouth {
  const near = model.prototypes
    .map((p) => ({ p, dist: squared(v, p.f) }))
    .sort((x, y) => x.dist - y.dist)
    .slice(0, model.k);
  const weight = near.reduce((s, { p }) => s + p.n, 0);
  let best = 0;
  let bestD = Infinity;
  model.mouths.forEach((_, i) => {
    const d = near.reduce((s, { p }) => s + p.d[i]! * p.n, 0) / weight;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return model.mouths[best]!;
}

function squared(a: number[], b: number[]): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += (a[i]! - b[i]!) ** 2;
  return s;
}

// ── what the audio looks like ──

const ENVELOPE_POINTS = 48; // uniform on the Bark scale, 100 Hz to 3.5 kHz
const SCALARS = 3; // log level, voicing, brightness
const QUIET = 0.02; // below this share of the loudest frame no envelope: zeros (silence has no spectrum to read)
// the weights of the vector's parts, chosen on the corpus (22/10): the frame's own scalars count more than its
// envelope, a neighbour brings its scalars and every sixth point of its envelope
const OWN_SCALARS = 2;
const NEIGHBOUR_SCALARS = 1.4;
const NEIGHBOUR_ENVELOPE = 0.7;
const NEIGHBOUR_STRIDE = 6;

const vectorWidth = (neighbours: number) =>
  ENVELOPE_POINTS + SCALARS + neighbours * (SCALARS + Math.ceil(ENVELOPE_POINTS / NEIGHBOUR_STRIDE));

/** Per FRAME_MS: the envelope (zeros when quiet), log10 of the level against the loudest frame, voicing, centroid / 4 kHz. */
export function frameFeatures(pcm: Float32Array, rate: number): number[][] {
  const hop = Math.round((rate * FRAME_MS) / 1000);
  const frames = Array.from({ length: Math.ceil(pcm.length / hop) }, (_, k) =>
    pcm.subarray(k * hop, Math.min(pcm.length, (k + 1) * hop)));
  const levels = frames.map(rms);
  const loudest = Math.max(1e-9, ...levels);
  return frames.map((f, k) => {
    const level = levels[k]! / loudest;
    const env = level > QUIET ? envelope(f, rate) : null;
    return [...(env ?? new Array<number>(ENVELOPE_POINTS).fill(0)), Math.log10(level + 1e-3), voicing(f, rate),
      centroid(f, rate) / 4000];
  });
}

/** The vector of frame `k` (clamped into the clause): its own features weighted, then each neighbour's in brief. */
export function frameVector(features: number[][], k: number, model: Pick<MouthModel, 'context'>): number[] {
  const at = (j: number) => features[Math.min(features.length - 1, Math.max(0, j))]!;
  const own = at(k);
  const v = [...own.slice(0, ENVELOPE_POINTS), ...own.slice(ENVELOPE_POINTS).map((x) => x * OWN_SCALARS)];
  for (const offset of model.context) {
    const n = at(k + offset);
    v.push(...n.slice(ENVELOPE_POINTS).map((x) => x * NEIGHBOUR_SCALARS));
    for (let i = 0; i < ENVELOPE_POINTS; i += NEIGHBOUR_STRIDE) v.push(n[i]! * NEIGHBOUR_ENVELOPE);
  }
  return v;
}

function rms(x: Float32Array): number {
  let s = 0;
  for (const v of x) s += v * v;
  return x.length ? Math.sqrt(s / x.length) : 0;
}

/** The normalised autocorrelation's highest peak between 70 and 300 Hz: near 1 voiced, near 0 noise. */
export function voicing(x: Float32Array, rate: number): number {
  const lo = Math.floor(rate / 300);
  const hi = Math.min(x.length - 1, Math.ceil(rate / 70));
  let energy = 0;
  for (const v of x) energy += v * v;
  if (energy <= 0) return 0;
  let best = 0;
  for (let lag = lo; lag <= hi; lag++) {
    let s = 0;
    for (let i = 0; i + lag < x.length; i++) s += x[i]! * x[i + lag]!;
    best = Math.max(best, s / energy);
  }
  return best;
}

/** Spectral centroid (Hz) by a plain DFT on 128 bins: enough to tell hiss from voice. */
export function centroid(x: Float32Array, rate: number): number {
  const bins = 128;
  let num = 0;
  let den = 0;
  for (let b = 1; b < bins; b++) {
    const w = (Math.PI * b) / bins;
    let re = 0;
    let im = 0;
    for (let i = 0; i < x.length; i++) {
      re += x[i]! * Math.cos(w * i);
      im -= x[i]! * Math.sin(w * i);
    }
    const mag = Math.hypot(re, im);
    num += mag * ((b * rate) / 2 / bins);
    den += mag;
  }
  return den > 0 ? num / den : 0;
}

const LPC_ORDER = 10;
const LPC_RATE = 8000; // the frame is decimated to about this: the resolution goes where the vowels differ

const bark = (f: number) => 13 * Math.atan(0.00076 * f) + 3.5 * Math.atan((f / 7500) ** 2);
const GRID = (() => {
  const lo = bark(100);
  const hi = bark(3500);
  const hz: number[] = [];
  for (let f = 100, k = 0; k < ENVELOPE_POINTS; f += 5) {  // invert bark() by search: it is monotonic
    if (bark(f) >= lo + ((hi - lo) * k) / (ENVELOPE_POINTS - 1)) {
      hz.push(f);
      k++;
    }
  }
  return hz;
})();

/** log10 |1 / A(f)| on GRID, its mean removed: the shape of the spectrum, not its level. */
function shape(a: number[], rate: number): number[] {
  const out = GRID.map((f) => {
    const w = (2 * Math.PI * f) / rate;
    let re = 0;
    let im = 0;
    for (let j = 0; j < a.length; j++) {
      re += a[j]! * Math.cos(w * j);
      im -= a[j]! * Math.sin(w * j);
    }
    return -Math.log10(Math.max(1e-12, Math.hypot(re, im)));
  });
  const mean = out.reduce((s, v) => s + v, 0) / out.length;
  return out.map((v) => v - mean);
}

/**
 * The spectral envelope of a frame (see `shape`): LPC of order 10 on the frame decimated to ~8 kHz, pre-emphasised
 * and windowed. Whole envelopes are compared, not peaks: in o and u the first two resonances are so close that the
 * envelope shows one peak, and a peak picker takes the third for the second. Null for a frame with no energy.
 */
export function envelope(x: Float32Array, rate: number): number[] | null {
  const step = Math.max(1, Math.round(rate / LPC_RATE));
  const y: number[] = [];
  for (let i = 0; i < x.length; i += step) {
    let s = 0;
    for (let j = 0; j < step && i + j < x.length; j++) s += x[i + j]!;
    y.push(s / step);
  }
  const m = y.length;
  if (m < LPC_ORDER + 2) return null;
  const z = y.map((v, i) => (v - (i ? 0.63 * y[i - 1]! : 0)) * (0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (m - 1))));
  const r = Array.from({ length: LPC_ORDER + 1 }, (_, lag) => {
    let s = 0;
    for (let i = 0; i + lag < m; i++) s += z[i]! * z[i + lag]!;
    return s;
  });
  if (r[0]! <= 0) return null;
  // Levinson-Durbin
  let a = [1];
  let err = r[0]!;
  for (let i = 1; i <= LPC_ORDER; i++) {
    let acc = r[i]!;
    for (let j = 1; j < i; j++) acc += a[j]! * r[i - j]!;
    const k = -acc / err;
    const next = [...a, k];
    for (let j = 1; j < i; j++) next[j] = a[j]! + k * a[i - j]!;
    a = next;
    err *= 1 - k * k;
    if (err <= 0) return null;
  }
  return shape(a, rate / step);
}
